import * as Phaser from "phaser-ce"
import { CCUtils } from "../../common/CCUtils"
import { COLOR } from "../../common/Color"
import { log } from "../../common/Logging"
import { Align, FontType, TextUtils } from "../../common/TextUtils"
import { MakeFriendAttribute } from "../activities/make-friend/MakeFriendTypes"
import { GenericAssets } from "../generic-sprites/GenericAssets"
import { Scale } from "./Scale"
import { Sprites } from "./Sprites"

export class PhaserTextConfig {
    x?: number = 0
    y?: number = 0
    alpha?: number = 1
    fontPx?: number = 24
    type?: FontType = FontType.BODY
    color?: string = COLOR.black
    align?: Align = Align.LEFT
    weight?: "bold" | "800" | "normal" = "normal"
    style?: "Black"
    wordWrapWidth?: number
    group?: Phaser.Group
    debug?: boolean
    centerInWorldXOnly?: boolean
    italic?: boolean = false
}

export class PhaserText {

    /**
     *
     * @param {Phaser.Game} game
     * @param {string} text
     * @param {number} x
     * @param {number} y
     * @param {number} alpha
     * @param {number} fontPx
     * @param {FontType} type
     * @param {COLOR} color
     * @param {string} align
     * @param weight
     * @param style
     * @param {number} wordWrapWidth -- Percentage of screen width as a decimal.
     * @param {Phaser.Group} group
     * @param debug
     * @param centerInWorldXOnly
     * @param italic
     * @returns {Phaser.Text}
     */
    static create = (
        game: Phaser.Game, text: string | MakeFriendAttribute,
        {
            x = 0, y = 0, alpha = 1, fontPx = 24, type = FontType.BODY, color = COLOR.black, align = Align.LEFT, weight = "normal", style,
            wordWrapWidth, group, debug = false, centerInWorldXOnly = false, italic,
        }: PhaserTextConfig = {}
    ): Phaser.Text => {
        const fontStyle = TextUtils.font(type, fontPx, style)

        // log.debug("Making text with style: " + fontStyle + " COLOR: " + fontColor + " and wrapWidth: " + wordWrapWidth)
        const textSprite = game.add.text(Math.floor(x), Math.floor(y), text,
            {
                font: fontStyle, fill: color, align,
                wordWrap: !!wordWrapWidth, wordWrapWidth: Scale.x(wordWrapWidth),
                fontStyle: italic ? "italic" : "",
            },
            group)
        textSprite.fontWeight = weight
        textSprite.alpha = alpha
        if (centerInWorldXOnly) {
            Sprites.centerInWorldXOnly(textSprite)
        }

        if (CCUtils.debug && debug) {
            Sprites.enableDebug(textSprite)
        }

        return textSprite
    }

    static bulletList = (game: Phaser.Game, config: PhaserTextConfig, spacing = Scale.y(0.005)) => {
        const {y, ...rest} = config
        let cumulativeTextHeight = 0
        return (text: string, indent?: boolean): Phaser.Text => {
            const textSprite = PhaserText.withBullet(
                game,
                text,
                {
                    y: y + cumulativeTextHeight + (cumulativeTextHeight === 0 ? 0 : spacing),
                    ...rest
                },
                indent
            ).text
            cumulativeTextHeight += textSprite.height + spacing
            log.debug("Bullet text height " + textSprite.height)
            return textSprite
        }
    }

    static list = (game: Phaser.Game, config: PhaserTextConfig, spacing = Scale.y(0.005)) => {
        const {y, ...rest} = config
        let cumulativeTextHeight = 0
        return (text: string): Phaser.Text => {
            const textSprite = PhaserText.create(
                game,
                text,
                {
                    y: y + cumulativeTextHeight + (
                        cumulativeTextHeight === 0 ? 0 : spacing
                    ),
                    ...rest
                },
            )
            cumulativeTextHeight += textSprite.height + spacing
            log.debug("List text height " + textSprite.height)
            return textSprite
        }
    }

    static orderedTextList = (game: Phaser.Game, config: PhaserTextConfig, spacing = Scale.y(0.005), letters = true) => {
        const {y, ...rest} = config
        let cumulativeTextHeight = 0
        let letter = "A"
        let numberHeading = 1

        return (text: string): Phaser.Text => {
            const textValue = PhaserText.withLetters(game, text, letters ? letter : numberHeading.toString(), {
                y: y + cumulativeTextHeight + (cumulativeTextHeight === 0 ? 0 : spacing),
                ...rest
            }).text
            cumulativeTextHeight += textValue.height + spacing
            letter = TextUtils.incrementLetter(letter)
            numberHeading++
            return textValue
        }
    }

    private static withLetters = (game: Phaser.Game, text: string, letter: string, config: PhaserTextConfig): { text: Phaser.Text, bullet: Phaser.Text } => {
        const letterSprite = PhaserText.create(game, `${letter})`, config)
        const textSprite = PhaserText.create(game, text, config)
        textSprite.x = Sprites.toRightOf(letterSprite, 0.01)
        return {text: textSprite, bullet: letterSprite}
    }

    private static withBullet = (
        game: Phaser.Game, textValue: string, config: PhaserTextConfig, indent = false
    ): { text: Phaser.Text, bullet: Phaser.Sprite } => {
        const {group, x, y, fontPx} = config
        const textGroup = group ? group : game.add.group()
        const bulletAsset = indent ? GenericAssets.SMALL_BULLET : GenericAssets.BULLET
        // Checks for font size value in config, set to default text size if not set
        const fontSize = fontPx ? fontPx : 24
        const text = PhaserText.create(game, textValue, config)
        const bullet = textGroup.create(x, y + Scale.y(0.012 * fontSize / 18), bulletAsset)
        Scale.scaleSprite(bullet, 0.3)
        if (indent) {
            bullet.x = Sprites.toRightOf(bullet, 0.01)
        }
        text.x = Sprites.toRightOf(bullet, 0.01)
        return {text, bullet}
    }
}
